/**
 * @fileoverview MyStableNew — Redesigned My Stable page
 *
 * Cinematic stable background, card-based syndicate entries,
 * and a slide-in detail panel for per-entry deep dive.
 *
 * Layout:
 *   - Full-width cinematic hero (stables.png, darkened overlay)
 *   - Headline stats bar: Total Value / Games Played / Active Now
 *   - Filter tabs: All / Active / Completed
 *   - Responsive card grid — each card is a "stable plaque"
 *   - Slide-in detail panel: Overview / My Horses / Race Results tabs
 */

import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { Link, useNavigate }    from 'react-router-dom';
import { onAuthStateChanged }   from 'firebase/auth';
import {
  collection, query, where, orderBy, onSnapshot, getDocs, doc, getDoc,
} from 'firebase/firestore';
import { auth, db }             from '../services/firebase';
import ShareStableButton        from '../components/share/ShareStableButton';
import {
  toMs, entryStatus, getEntryEarnings, buildStableCardMetrics,
} from '../utils/stableStatus';
import './MyStableNew.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(n) {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

function fmtDate(val) {
  const ms = toMs(val);
  if (!ms) return null;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(ms));
}

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_META = {
  active:    { label: 'Active',    colour: 'var(--accent-green)' },
  completed: { label: 'Completed', colour: 'var(--status-done)'  },
  upcoming:  { label: 'Upcoming',  colour: 'var(--accent-amber)' },
};

// ─── Progress bar ─────────────────────────────────────────────────────────────
// Simple settled/total bar used inside the Detail Panel overview, where
// there's no countdown/next-race context to show alongside it.

const ProgressBar = ({ settled, total }) => {
  const pctVal = total > 0 ? (settled / total) * 100 : 0;
  return (
    <div className="ms-progress">
      <div className="ms-progress-track">
        <div
          className={`ms-progress-fill ${pctVal >= 100 ? 'ms-progress-fill--done' : ''}`}
          style={{ width: `${pctVal}%` }}
        />
      </div>
      <span className="ms-progress-label">{settled}/{total} races</span>
    </div>
  );
};

// Richer race-progress block used on My Stable cards — resulted/total count,
// a coloured fill bar, and a countdown/next-race/"Completed" context line.
const RaceProgress = ({ metrics }) => {
  const { totalRaces, resultedRaceCount, progressPct, status, countdownLabel } = metrics;
  if (totalRaces === 0) return null;
  return (
    <div className="ms-card-progress">
      <div className="ms-card-progress-header">
        <span>Race Progress</span>
        <span>{resultedRaceCount} / {totalRaces} resulted</span>
      </div>
      <div className="ms-card-progress-track">
        <div
          className={`ms-card-progress-fill ${status === 'completed' ? 'ms-card-progress-fill--done' : ''}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>
      {countdownLabel && (
        <p className="ms-card-progress-context">{countdownLabel}</p>
      )}
    </div>
  );
};

// ─── Stable Card ──────────────────────────────────────────────────────────────

const StableCard = ({ entry, onOpen, user, races, now }) => {
  const metrics = buildStableCardMetrics(entry, races, now);
  const {
    status, statusLabel, virtualWinnings, resultedRaceCount, boardEnabled,
  } = metrics;
  const meta = STATUS_META[status];
  const hasEarnings   = virtualWinnings > 0;
  const winningsLabel = status !== 'completed' && resultedRaceCount === 0
    ? 'Pending'
    : fmt$(virtualWinnings);

  const gameName = entry.gameName
    || entry.slateId
      ?.replace(/^\d{4}-\d{2}-\d{2}_/, '')   // strip YYYY-MM-DD_ prefix
      ?.replace(/^\d{8}_/, '')                 // strip YYYYMMDD_ prefix
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, c => c.toUpperCase())
    || 'Showdown';

  const submittedDate = fmtDate(entry.submittedAt);

  return (
    <div
      className={`ms-card ms-card--${status}`}
      onClick={() => onOpen(entry)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen(entry)}
    >
      {/* Card header */}
      <div className="ms-card-header">
        <span className="ms-card-status" style={{ color: meta.colour }}>
          ● {statusLabel}
        </span>
        <span className={`ms-card-earnings ${hasEarnings ? 'ms-card-earnings--pos' : ''}`}>
          {status === 'completed'
            ? (hasEarnings ? `+${fmt$(virtualWinnings)}` : fmt$(virtualWinnings))
            : status === 'upcoming' && submittedDate
            ? `Entered ${submittedDate}`
            : ''}
        </span>
      </div>

      {/* Game name */}
      <h3 className="ms-card-name">{gameName}</h3>
      {submittedDate && status !== 'upcoming' && (
        <p className="ms-card-date">Entered {submittedDate}</p>
      )}

      {/* Stats row */}
      <div className="ms-card-stats">
        <div className="ms-card-stat">
          <span className="ms-card-stat-label">Invested</span>
          <span className="ms-card-stat-val">{fmt$(entry.totalSpent)}</span>
        </div>
        <div className="ms-card-stat">
          <span className="ms-card-stat-label">Remaining</span>
          <span className="ms-card-stat-val">{fmt$(entry.remainingBankroll)}</span>
        </div>
        <div className="ms-card-stat">
          <span className="ms-card-stat-label">Virtual Winnings</span>
          <span
            className="ms-card-stat-val"
            style={hasEarnings ? { color: 'var(--accent-green)' } : undefined}
          >
            {winningsLabel}
          </span>
        </div>
        {status === 'completed' && entry.finalRank && (
          <div className="ms-card-stat">
            <span className="ms-card-stat-label">Rank</span>
            <span className="ms-card-stat-val">#{entry.finalRank}</span>
          </div>
        )}
      </div>

      {/* Race progress */}
      <RaceProgress metrics={metrics} />

      {/* Footer actions */}
      <div className="ms-card-footer">
        <button className="ms-card-detail-btn" type="button">
          View Details →
        </button>
        <div onClick={e => e.stopPropagation()}>
          <ShareStableButton
            entry={entry}
            user={user}
            className="ms-card-share-btn"
            label="Share"
            compact
          />
        </div>
        {boardEnabled ? (
          <Link
            to={`/leaderboard/${entry.slateId}`}
            className="ms-card-board-btn"
            onClick={e => e.stopPropagation()}
          >
            🏆 Board
          </Link>
        ) : (
          <button
            type="button"
            className="ms-card-board-btn ms-card-board-btn--disabled"
            disabled
            onClick={e => e.stopPropagation()}
            title="Leaderboard opens after Race 1 results."
          >
            🏆 Board
          </button>
        )}
        {status === 'active' && (
          <Link
            to={`/slate/${entry.slateId}`}
            className="ms-card-edit-btn"
            onClick={e => e.stopPropagation()}
          >
            Edit →
          </Link>
        )}
      </div>
    </div>
  );
};

// ─── Detail Panel ─────────────────────────────────────────────────────────────

const DetailPanel = ({ entry, onClose, user, races }) => {
  const navigate = useNavigate();
  const [tab,         setTab]         = useState('overview');
  const [allocations, setAllocations] = useState({});
  const [loading,     setLoading]     = useState(true);

  // Load race allocations for the selected entry
  useEffect(() => {
    if (!entry) return;
    setLoading(true);
    setTab('overview');

    const allocRef = collection(db, 'player_entries', entry.id, 'race_allocations');

    getDocs(allocRef)
      .then(snap => {
        const map = {};
        snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
        setAllocations(map);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [entry]);

  if (!entry) return null;

  const gameName = entry.gameName
    || entry.slateId
      ?.replace(/^\d{4}-\d{2}-\d{2}_/, '')   // strip YYYY-MM-DD_ prefix
      ?.replace(/^\d{8}_/, '')                 // strip YYYYMMDD_ prefix
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, c => c.toUpperCase())
    || 'Showdown';

  const cardMetrics = buildStableCardMetrics(entry, races);
  const { status, boardEnabled, virtualWinnings: earnings } = cardMetrics;
  const meta     = STATUS_META[status];
  const settled  = entry.racesSettledCount || entry.racesSettled || 0;
  const total    = entry.totalRaces || 0;

  // All runners across all races
  const allRunners = useMemo(() => {
    const runners = [];
    Object.entries(allocations).forEach(([raceId, alloc]) => {
      Object.entries(alloc.runners || {}).forEach(([runnerId, runner]) => {
        runners.push({ raceId, runnerId, ...runner });
      });
    });
    return runners;
  }, [allocations]);

  // Active (non-voided) runners
  const activeRunners = allRunners.filter(
    r => r.status !== 'scratched_voided'
      && r.status !== 'non_runner_voided'
      && (r.equityPercent || 0) > 0
  );

  // Race results summary
  const raceResults = useMemo(() => {
    return Object.entries(allocations)
      .map(([raceId, alloc]) => ({
        raceId,
        label:   raceId.replace(/.*_R(\d+)$/, 'Race $1'),
        settled: alloc.status === 'resulted' || alloc.earnings != null,
        earnings: alloc.earnings || 0,
        spent:   alloc.totalSpentOnRace || 0,
        runners: Object.entries(alloc.runners || {})
          .filter(([, r]) => r.status !== 'scratched_voided')
          .map(([id, r]) => ({ id, ...r })),
      }))
      .sort((a, b) => {
        const na = parseInt(a.raceId.match(/_R(\d+)$/)?.[1] || 0);
        const nb = parseInt(b.raceId.match(/_R(\d+)$/)?.[1] || 0);
        return na - nb;
      });
  }, [allocations]);

  const runnerStatusIcon = r => {
    if (r.status === 'scratched_voided' || r.status === 'non_runner_voided') return '❌';
    if (r.earnings > 0)       return '🥇';
    if (r.status === 'resulted') return '✓';
    return '🐎';
  };

  const runnerStatusColour = r => {
    if (r.earnings > 0)          return 'var(--accent-gold)';
    if (r.status === 'resulted') return 'var(--accent-green)';
    return 'var(--text-secondary)';
  };

  return (
    <>
      {/* Backdrop */}
      <div className="ms-panel-backdrop" onClick={onClose} />

      {/* Panel */}
      <div className="ms-panel">

        {/* Panel header */}
        <div className="ms-panel-header">
          <div>
            <span className="ms-panel-status" style={{ color: meta.colour }}>
              ● {meta.label}
            </span>
            <h2 className="ms-panel-name">{gameName}</h2>
          </div>
          <button className="ms-panel-close" onClick={onClose} type="button">✕</button>
        </div>

        {/* Quick stats bar */}
        <div className="ms-panel-stats">
          <div className="ms-panel-stat">
            <span className="ms-panel-stat-label">Invested</span>
            <span className="ms-panel-stat-val">{fmt$(entry.totalSpent)}</span>
          </div>
          <div className="ms-panel-stat">
            <span className="ms-panel-stat-label">Remaining</span>
            <span className="ms-panel-stat-val">{fmt$(entry.remainingBankroll)}</span>
          </div>
          <div className="ms-panel-stat">
            <span className="ms-panel-stat-label">Earnings</span>
            <span className={`ms-panel-stat-val ${earnings > 0 ? 'ms-panel-stat-val--pos' : ''}`}>
              {fmt$(earnings)}
            </span>
          </div>
          <div className="ms-panel-stat">
            <span className="ms-panel-stat-label">Progress</span>
            <span className="ms-panel-stat-val">{settled}/{total}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="ms-panel-tabs">
          {[
            { key: 'overview', label: '📋 Overview'      },
            { key: 'horses',   label: '🐎 My Horses'     },
            { key: 'results',  label: '🏁 Race Results'  },
          ].map(t => (
            <button
              key={t.key}
              className={`ms-panel-tab ${tab === t.key ? 'ms-panel-tab--active' : ''}`}
              onClick={() => setTab(t.key)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="ms-panel-content">

          {loading ? (
            <div className="ms-panel-loading">
              <div className="ss-spinner" />
              <p>Loading stable data…</p>
            </div>

          ) : tab === 'overview' ? (
            <div className="ms-panel-overview">
              <div className="ms-overview-grid">
                <div className="ms-overview-item">
                  <span className="ms-overview-label">Total Bankroll</span>
                  <span className="ms-overview-val">{fmt$(entry.totalBankroll)}</span>
                </div>
                <div className="ms-overview-item">
                  <span className="ms-overview-label">Total Spent</span>
                  <span className="ms-overview-val">{fmt$(entry.totalSpent)}</span>
                </div>
                <div className="ms-overview-item">
                  <span className="ms-overview-label">Remaining</span>
                  <span className="ms-overview-val">{fmt$(entry.remainingBankroll)}</span>
                </div>
                <div className="ms-overview-item">
                  <span className="ms-overview-label">Virtual Earnings</span>
                  <span className={`ms-overview-val ${earnings > 0 ? 'ms-overview-val--pos' : ''}`}>
                    {fmt$(earnings)}
                  </span>
                </div>
                <div className="ms-overview-item">
                  <span className="ms-overview-label">Races Settled</span>
                  <span className="ms-overview-val">{settled} / {total}</span>
                </div>
                {entry.finalRank && (
                  <div className="ms-overview-item">
                    <span className="ms-overview-label">Final Rank</span>
                    <span className="ms-overview-val">#{entry.finalRank}</span>
                  </div>
                )}
              </div>

              {total > 0 && (
                <div style={{ marginTop: 20 }}>
                  <ProgressBar settled={settled} total={total} />
                </div>
              )}

              <div className="ms-panel-actions">
                {status === 'active' && (
                  <button
                    className="ms-action-btn ms-action-btn--primary"
                    onClick={() => navigate(`/slate/${entry.slateId}`)}
                    type="button"
                  >
                    Edit Selections →
                  </button>
                )}
                {boardEnabled ? (
                  <Link
                    to={`/leaderboard/${entry.slateId}`}
                    className="ms-action-btn ms-action-btn--outline"
                  >
                    View Leaderboard →
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="ms-action-btn ms-action-btn--outline ms-action-btn--disabled"
                    disabled
                    title="Leaderboard opens after Race 1 results."
                  >
                    View Leaderboard →
                  </button>
                )}
                <ShareStableButton entry={entry} user={user} />
              </div>
            </div>

          ) : tab === 'horses' ? (
            <div className="ms-panel-horses">
              {activeRunners.length === 0 ? (
                <div className="ms-panel-empty">
                  <span>🐎</span>
                  <p>No active runners in this entry.</p>
                </div>
              ) : (
                <div className="ms-horse-list">
                  {activeRunners.map(r => (
                    <div key={`${r.raceId}_${r.runnerId}`} className="ms-horse-row">
                      <div className="ms-horse-left">
                        <span className="ms-horse-icon" style={{ color: runnerStatusColour(r) }}>
                          {runnerStatusIcon(r)}
                        </span>
                        <div className="ms-horse-info">
                          <span className="ms-horse-name">{r.runnerId}</span>
                          <span className="ms-horse-race">
                            {r.raceId.replace(/.*_R(\d+)$/, 'Race $1')}
                          </span>
                        </div>
                      </div>
                      <div className="ms-horse-right">
                        <div className="ms-horse-stat">
                          <span className="ms-horse-stat-label">Equity</span>
                          <span className="ms-horse-stat-val">
                            {(r.equityPercent || 0).toFixed(0)}%
                          </span>
                        </div>
                        <div className="ms-horse-stat">
                          <span className="ms-horse-stat-label">Cost</span>
                          <span className="ms-horse-stat-val">{fmt$(r.purchaseCost)}</span>
                        </div>
                        {r.earnings > 0 && (
                          <div className="ms-horse-stat">
                            <span className="ms-horse-stat-label">Earned</span>
                            <span className="ms-horse-stat-val ms-horse-stat-val--pos">
                              {fmt$(r.earnings)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          ) : tab === 'results' ? (
            <div className="ms-panel-results">
              {raceResults.length === 0 ? (
                <div className="ms-panel-empty">
                  <span>🏁</span>
                  <p>No race results yet.</p>
                </div>
              ) : (
                <div className="ms-results-list">
                  {raceResults.map(race => (
                    <div key={race.raceId} className="ms-result-row">
                      <div className="ms-result-header">
                        <span className="ms-result-label">{race.label}</span>
                        {race.settled ? (
                          <span className={`ms-result-earnings ${race.earnings > 0 ? 'ms-result-earnings--pos' : ''}`}>
                            {race.earnings > 0 ? `+${fmt$(race.earnings)}` : fmt$(race.earnings)}
                          </span>
                        ) : (
                          <span className="ms-result-pending">Pending</span>
                        )}
                      </div>
                      <div className="ms-result-runners">
                        {race.runners.map(r => (
                          <span key={r.id} className="ms-result-runner">
                            {r.id} ({(r.equityPercent || 0).toFixed(0)}%)
                          </span>
                        ))}
                      </div>
                      <div className="ms-result-meta">
                        <span className="ms-result-spent">Invested {fmt$(race.spent)}</span>
                      </div>
                    </div>
                  ))}

                  {/* Running total */}
                  <div className="ms-results-total">
                    <span>Total Earnings</span>
                    <span className={earnings > 0 ? 'ms-result-earnings--pos' : ''}>
                      {fmt$(earnings)}
                    </span>
                  </div>
                </div>
              )}
            </div>

          ) : null}
        </div>
      </div>
    </>
  );
};

// ─── MyStableNew ──────────────────────────────────────────────────────────────

export const MyStableNew = () => {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [stables,     setStables]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('active');
  const [selected,    setSelected]    = useState(null);
  const [racesBySlate, setRacesBySlate] = useState({});
  const [now,          setNow]          = useState(Date.now());
  const [stableSlug,   setStableSlug]   = useState(null);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      if (!user) setLoading(false);
    });
  }, []);

  // ── Public profile link (Phase 7) ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) { setStableSlug(null); return; }
    getDoc(doc(db, 'users', currentUser.uid))
      .then(snap => setStableSlug(snap.exists() ? (snap.data().stableSlug || null) : null))
      .catch(() => setStableSlug(null));
  }, [currentUser]);

  // ── Entries ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'player_entries'),
      where('userId', '==', currentUser.uid),
      orderBy('submittedAt', 'desc')
    );
    return onSnapshot(q, snap => {
      setStables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [currentUser]);

  // ── Countdown tick — drives active-card badges every 30s ──────────────────
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // ── Race timing/status per non-completed slate ─────────────────────────────
  // Needed for both the Board-button gate (Race 1 resulted?) and the
  // countdown/live badge on active cards. Completed entries never need this
  // (Board is always enabled once complete), so we only fetch for the rest.
  const openSlateIdsKey = useMemo(() => {
    const ids = [...new Set(
      stables.filter(e => entryStatus(e) !== 'completed').map(e => e.slateId)
    )].sort();
    return ids.join(',');
  }, [stables]);

  useEffect(() => {
    const slateIds = openSlateIdsKey ? openSlateIdsKey.split(',') : [];
    if (slateIds.length === 0) { setRacesBySlate({}); return undefined; }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(slateIds.map(async (slateId) => {
        try {
          const snap = await getDocs(query(
            collection(db, 'game_slates', slateId, 'races'),
            orderBy('raceNumber', 'asc')
          ));
          return [slateId, snap.docs.map(d => ({ id: d.id, ...d.data() }))];
        } catch (e) {
          console.error('[MyStableNew] races load error:', slateId, e.code, e.message);
          return [slateId, []];
        }
      }));
      if (!cancelled) setRacesBySlate(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [openSlateIdsKey]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (filter === 'active')    return stables.filter(e => entryStatus(e) === 'active');
    if (filter === 'completed') return stables.filter(e => entryStatus(e) === 'completed');
    return stables;
  }, [stables, filter]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  // Total Earnings responds to the selected tab — Active tab sums only
  // active stables, Completed only completed, All sums everything. Always
  // uses the canonical settlement figure (financialAudit.totalPrizeDividendsWon
  // via getEntryEarnings), never remaining-wallet math.
  const totalEarnings = filtered.reduce((s, e) => s + getEntryEarnings(e), 0);
  const activeCount   = stables.filter(e => entryStatus(e) === 'active').length;
  const earningsLabel = filter === 'active' ? 'Active Earnings'
    : filter === 'completed' ? 'Completed Earnings'
    : 'Total Earnings';

  const handleOpen  = useCallback(entry => setSelected(entry), []);
  const handleClose = useCallback(() => setSelected(null),     []);

  if (currentUser === undefined || loading) {
    return (
      <div className="ms-page">
        <div className="ms-loading">
          <div className="ss-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="ms-page">

      {/* ── Cinematic hero ──────────────────────────────────────────── */}
      <div className="ms-hero">
        <div className="ms-hero-bg" />
        <div className="ms-hero-overlay" />
        <div className="ms-hero-content">
          <div className="ms-hero-left">
            <h1 className="ms-hero-title">
              <span className="ms-hero-horseshoe">🐎</span> My Stable
            </h1>
            <p className="ms-hero-sub">
              {stables.length} syndicate{stables.length !== 1 ? 's' : ''} · {activeCount} active
            </p>
            {stableSlug && (
              <Link to={`/stable/${stableSlug}`} className="ms-hero-profile-link">
                View Public Profile →
              </Link>
            )}
          </div>
          <div className="ms-hero-stats">
            <div className="ms-hero-stat">
              <span className="ms-hero-stat-val">{stables.length}</span>
              <span className="ms-hero-stat-label">Games Played</span>
            </div>
            <div className="ms-hero-stat-divider" />
            <div className="ms-hero-stat">
              <span className="ms-hero-stat-val ms-hero-stat-val--active">{activeCount}</span>
              <span className="ms-hero-stat-label">Active Now</span>
            </div>
            <div className="ms-hero-stat-divider" />
            <div className="ms-hero-stat">
              <span className={`ms-hero-stat-val ${totalEarnings > 0 ? 'ms-hero-stat-val--pos' : ''}`}>
                {fmt$(totalEarnings)}
              </span>
              <span className="ms-hero-stat-label">{earningsLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter tabs ─────────────────────────────────────────────── */}
      <div className="ms-filters">
        {[
          { key: 'all',       label: `All (${stables.length})`  },
          { key: 'active',    label: `Active (${activeCount})`   },
          { key: 'completed', label: 'Completed'                 },
        ].map(f => (
          <button
            key={f.key}
            className={`ms-filter-btn ${filter === f.key ? 'ms-filter-btn--active' : ''}`}
            onClick={() => setFilter(f.key)}
            type="button"
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Card grid ───────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="ms-empty">
          <span style={{ fontSize: 40 }}>🐎</span>
          {filter === 'active' ? (
            <>
              <p className="ms-empty-title">No active stables yet</p>
              <p className="ms-empty-sub">Enter a Showdown to build your first syndicate.</p>
              <Link to="/showdowns" className="ms-browse-btn">Enter a Showdown</Link>
            </>
          ) : filter === 'completed' ? (
            <>
              <p className="ms-empty-title">No completed stables yet</p>
              <Link to="/showdowns" className="ms-browse-btn">Browse Showdowns →</Link>
            </>
          ) : (
            <>
              <p className="ms-empty-title">No entries yet</p>
              <Link to="/showdowns" className="ms-browse-btn">Browse Showdowns →</Link>
            </>
          )}
        </div>
      ) : (
        <div className="ms-grid">
          {filtered.map(entry => (
            <StableCard
              key={entry.id}
              entry={entry}
              onOpen={handleOpen}
              user={currentUser}
              races={racesBySlate[entry.slateId]}
              now={now}
            />
          ))}
        </div>
      )}

      {/* ── Browse CTA ──────────────────────────────────────────────── */}
      {stables.length > 0 && (
        <div className="ms-browse-cta">
          <span className="ms-browse-icon">🏇</span>
          <div>
            <p className="ms-browse-title">Want to enter more showdowns?</p>
            <p className="ms-browse-sub">Browse upcoming races and build your stable</p>
          </div>
          <Link to="/showdowns" className="ms-browse-link">Browse Showdowns →</Link>
        </div>
      )}

      {/* ── Detail panel ────────────────────────────────────────────── */}
      {selected && (
        <DetailPanel
          entry={selected}
          onClose={handleClose}
          user={currentUser}
          races={racesBySlate[selected.slateId]}
        />
      )}
    </div>
  );
};

export default MyStableNew;
